import { io } from 'socket.io-client';
import { API_URL } from '../../utils/axios';

// Single shared Socket.IO connection to the backend, reused across the WhatsApp
// tabs. The server emits: wa:message (open-thread room), wa:inbox (global list),
// wa:status, wa:template_status, wa:broadcast.
// Socket.IO lives at the API origin (not under /api), so strip the /api suffix.
const SOCKET_ORIGIN = API_URL.replace(/\/api\/?$/, '');

// Preserve the socket instance across HMR hot-reloads by storing it on window.
// Without this, every React module reload (edit + save during dev) creates a
// brand-new Socket.IO client, causing the rapid connect/disconnect loop visible
// in the backend logs. The global is harmless in production (SSR guard via typeof).
let socket = typeof window !== 'undefined' ? (window.__wa_socket__ || null) : null;

export const getWaSocket = () => {
  if (!socket) {
    socket = io(SOCKET_ORIGIN, {
      path: '/socket.io',
      // Polling-first lets the connection establish immediately via HTTP, then
      // upgrades to WebSocket in the background. Starting with 'websocket' first
      // causes handshake failures in local/proxied environments that loop rapidly.
      transports: ['polling', 'websocket'],
      auth: { token: localStorage.getItem('admin_token') || '' },
      autoConnect: true,
    });
    if (typeof window !== 'undefined') {
      window.__wa_socket__ = socket;
    }
  }
  return socket;
};

// Subscribe to an event; returns an unsubscribe function for cleanup in useEffect.
export const onWaEvent = (event, handler) => {
  const s = getWaSocket();
  s.on(event, handler);
  return () => s.off(event, handler);
};

// Join / leave a conversation room so the OPEN thread receives its own live
// messages (wa:message) instead of every socket getting every thread. The
// conversation LIST keeps updating from the global wa:inbox event.
export const joinWaRoom = (conversationId) => {
  if (conversationId) getWaSocket().emit('wa:join', conversationId);
};
export const leaveWaRoom = (conversationId) => {
  if (conversationId) getWaSocket().emit('wa:leave', conversationId);
};

// Run a handler whenever the socket RE-connects after a drop (e.g. a server
// redeploy). socket.io buffers nothing across a disconnect, so the UI uses this
// to re-fetch + re-join — otherwise messages emitted while offline are missed.
// Returns an unsubscribe function for cleanup.
export const onWaReconnect = (handler) => {
  const s = getWaSocket();
  s.io.on('reconnect', handler);
  return () => s.io.off('reconnect', handler);
};
